/**
 * Owner: Masa Bayu
 * File: DedicatedAnalyticsScreen.tsx
 * Date:Time: 2026-10-17 03:38
 * Description: Dedicated author-analytics screen — sales rollups + supplied audience
 *   aggregates in a chart frame. No Reader telemetry. Profile palette.
 */

import { useState } from 'react';
import {
  getDemoAnalyticsSummary,
  projectDemoAuthorAnalytics,
  resetDemoAuthorAnalytics,
} from '../demo-author-analytics';
import { formatMinor } from './dedicated-format';
import './dedicated.css';

export interface DedicatedAnalyticsScreenProps {
  readonly onNavigate: (path: string) => void;
}

export function DedicatedAnalyticsScreen({ onNavigate }: DedicatedAnalyticsScreenProps) {
  const [status, setStatus] = useState(
    'AuthorAnalyticsSlice aggregates SaleRecords — not raw Reader telemetry.',
  );
  const [, setRevision] = useState(0);
  const summary = getDemoAnalyticsSummary();

  const refresh = (nextStatus: string) => {
    setStatus(nextStatus);
    setRevision((value) => value + 1);
  };

  const maxUnits =
    summary === null
      ? 0
      : summary.chartSeries.reduce((peak, point) => Math.max(peak, point.unitsSold), 0);

  return (
    <div className="ddx">
      <header>
        <p className="ddx__eyebrow">Author · Insight</p>
        <h2 className="ddx__title">Analytics</h2>
        <p className="ddx__lede">
          Sales rollups and supplied audience aggregates only. Privacy-safe slices — no
          reading-position dump or Reader execution from this surface.
        </p>
      </header>

      {summary !== null ? (
        <div className="ddx__stats">
          <div className="ddx__stat ddx__stat--pink">
            <span className="ddx__stat-value">{String(summary.slice.totalUnitsSold)}</span>
            <span className="ddx__stat-label">Units sold</span>
          </div>
          <div className="ddx__stat ddx__stat--violet">
            <span className="ddx__stat-value">
              {formatMinor(summary.slice.totalSellerSubtotalMinorUnits, summary.slice.currency)}
            </span>
            <span className="ddx__stat-label">Revenue</span>
          </div>
        </div>
      ) : null}

      <p className="ddx__status" role="status" aria-live="polite">
        {status}
      </p>

      <div className="ddx__actions">
        <button
          type="button"
          className="ddx__cta ddx__cta--solid"
          onClick={() => {
            refresh(projectDemoAuthorAnalytics());
          }}
        >
          Project from sales
        </button>
        <button
          type="button"
          className="ddx__cta"
          onClick={() => {
            resetDemoAuthorAnalytics();
            refresh('Analytics cleared.');
          }}
        >
          Reset
        </button>
        <button
          type="button"
          className="ddx__cta"
          onClick={() => {
            onNavigate('/author/sales');
          }}
        >
          Sales
        </button>
        <button
          type="button"
          className="ddx__cta"
          onClick={() => {
            onNavigate('/author');
          }}
        >
          Dashboard
        </button>
      </div>

      {summary === null ? (
        <div className="ddx__empty">
          <h3>No analytics yet</h3>
          <p>Project from sales to build the privacy-safe rollup slices.</p>
        </div>
      ) : (
        <>
          <section className="ddx__chart" aria-label="Sales by title">
            <h3 className="ddx__head">Sales by title</h3>
            {summary.chartSeries.map((point) => {
              const pct = maxUnits === 0 ? 0 : Math.round((point.unitsSold / maxUnits) * 100);
              return (
                <div className="ddx__bar" key={point.label}>
                  <span className="ddx__bar-label">
                    <span>{point.label}</span>
                    <span>
                      {String(point.unitsSold)} units ·{' '}
                      {formatMinor(point.sellerSubtotalMinorUnits, summary.slice.currency)}
                    </span>
                  </span>
                  <span className="ddx__bar-track">
                    <span className="ddx__bar-fill" style={{ width: `${String(pct)}%` }} />
                  </span>
                </div>
              );
            })}
          </section>
          <p className="ddx__status">
            Audience (supplied aggregate): followers {String(summary.slice.followerCount ?? 0)} ·
            profile views {String(summary.slice.profileViews ?? 0)}
          </p>
        </>
      )}
    </div>
  );
}
